import { DataGridProps, NoRowsOverlayPropsOverrides } from "@mui/x-data-grid";
import { DataGrid, GridRowsProp, GridColDef } from "@mui/x-data-grid";
import { CustomTableWrapper } from "./styles";
import {
  Dispatch,
  HTMLAttributes,
  JSXElementConstructor,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import CustomPagination, { IListPerPagesNumber } from "./CustomPagination";
import { Box, SxProps, Theme } from "@mui/material";
import ReservationFilters, {
  FilterType,
  ReservationFiltersState,
} from "../../components/ReservationManagement/ReservationFilters";
import { Loading } from "@components";

interface IProps extends DataGridProps {
  hasPages?: boolean;
  rows: GridRowsProp;
  columns: GridColDef[];
  disableColumnMenu?: boolean;
  disableColumnFilter?: boolean;
  disableColumnResize?: boolean;
  disableColumnSorting?: boolean;
  disableRowSelectionOnClick?: boolean;
  listPerPagesNumber?: IListPerPagesNumber[];
  HeaderTheTable?: string;
  onPerpageChange?: Dispatch<SetStateAction<string>>;
  perPage?: string;
  totalPages: number;
  setActivePage: Dispatch<SetStateAction<number>>;
  activePage: number;
  suffix?: any;
  CustomNoRowsOverlay?:
    | JSXElementConstructor<
        HTMLAttributes<HTMLDivElement> & {
          sx?: SxProps<Theme>;
        } & NoRowsOverlayPropsOverrides
      >
    | undefined;
  totalRecords?: number;
  // Filter props
  filters?: ReservationFiltersState;
  onFiltersChange?: (filters: ReservationFiltersState) => void;
  onClearFilters?: () => void;
  loading?: boolean;
  reservations?: any[]; // Add reservations data prop
  visibleFilters?: FilterType[];
  enumOptions?: string[];
  onNameSearchChange?: (searchTerm: string) => void; // Add name search callback
  DateFilterPlaceholder?: [string, string];
  ChannelFilterLabel?: string;
  PriceRangeFilterLabel?: string;
  EnumFilterLabel?: string;
  nameFilterPlaceholder?: string;
  showFilterBar?: boolean;
}

export default function CustomTable({
  hasPages = true,
  disableColumnFilter = true,
  disableColumnResize = true,
  disableColumnSorting = true,
  disableRowSelectionOnClick = true,
  disableColumnMenu = true,
  rows,
  listPerPagesNumber = [],
  HeaderTheTable,
  totalPages,
  onPerpageChange,
  perPage,
  setActivePage,
  activePage,
  CustomNoRowsOverlay,
  suffix = () => {},
  totalRecords,
  filters,
  onFiltersChange,
  onClearFilters,
  loading,
  reservations = [],
  visibleFilters = ["date", "channel", "name", "price"],
  enumOptions = [],
  onNameSearchChange,
  DateFilterPlaceholder,
  ChannelFilterLabel,
  PriceRangeFilterLabel,
  EnumFilterLabel,
  nameFilterPlaceholder,
  showFilterBar = true,
  ...rest
}: IProps) {
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [totalPages]);

  const RenderFilters = useMemo(() => {
    return (
      <ReservationFilters
        filters={
          filters ?? {
            dateRange: null,
            selectedChannels: [],
            selectedNames: [],
            priceRange: [0, 1000],
            enum: [],
          }
        }
        onFiltersChange={onFiltersChange ?? (() => {})}
        onClearFilters={onClearFilters ?? (() => {})}
        reservations={[]}
        visibleFilters={visibleFilters}
        enumOptions={enumOptions}
        onNameSearchChange={onNameSearchChange}
        DateFilterPlaceholder={DateFilterPlaceholder}
        ChannelFilterLabel={ChannelFilterLabel}
        PriceRangeFilterLabel={PriceRangeFilterLabel}
        EnumFilterLabel={EnumFilterLabel}
        nameFilterPlaceholder={nameFilterPlaceholder}
      />
    );
  }, [
    filters,
    onFiltersChange,
    onClearFilters,
    visibleFilters,
    enumOptions,
    onNameSearchChange,
    DateFilterPlaceholder,
    ChannelFilterLabel,
    PriceRangeFilterLabel,
    EnumFilterLabel,
    nameFilterPlaceholder,
  ]);

  return (
    <>
      <CustomTableWrapper>
        {HeaderTheTable && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            className="HeaderTheTable"
          >
            <p>{HeaderTheTable}</p>
            {typeof suffix === 'function' ? suffix() : suffix}
          </Box>
        )}
        {showFilterBar && RenderFilters}
        <DataGrid
          {...rest}
          rows={rows}
          disableColumnMenu={disableColumnMenu}
          disableColumnFilter={disableColumnFilter}
          disableColumnResize={disableColumnResize}
          disableColumnSorting={disableColumnSorting}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          hideFooterPagination
          key={windowSize}
          autoHeight
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
        />
        {loading && <Loading />}

        {hasPages ? (
          <CustomPagination
            count={totalPages}
            setActivePage={setActivePage}
            listPerPagesNumber={listPerPagesNumber}
            onPerpageChange={onPerpageChange}
            perPage={perPage}
            activePage={activePage}
            totalRecords={totalRecords}
          />
        ) : null}
      </CustomTableWrapper>
    </>
  );
}
